// Path: src/components/admin/LiveSupport.tsx
// New component for admin live chat support.
// Displays online users and provides a real-time chat interface.

import React, { useState, useEffect } from 'react';
import { useFirebase } from '../dev/Firebase/Firebase';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, orderBy, doc, updateDoc } from 'firebase/firestore';
import { List, Avatar, Button, Input, Card, Row, Col, Typography, Empty } from 'antd';
import { UserOutlined, MessageOutlined, SendOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Title, Text, Paragraph } = Typography;

// --- Styled Components ---
const LiveSupportContainer = styled.div`
  display: flex;
  height: calc(100vh - 150px); // Adjust height as needed
  gap: 24px;
`;

const UserListPanel = styled(Card)`
  width: 350px;
  display: flex;
  flex-direction: column;
  .ant-card-body {
    flex-grow: 1;
    overflow-y: auto;
    padding: 0;
  }
`;

const ChatPanel = styled(Card)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  .ant-card-body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding: 16px;
  }
`;

const MessageList = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 15px;
  margin-bottom: 15px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #f5f5f5;
`;

const MessageBubble = styled.div<{ $isAdmin: boolean }>`
  max-width: 80%;
  padding: 10px 15px;
  border-radius: 18px;
  line-height: 1.5;
  margin-bottom: 12px;
  color: ${({ $isAdmin }) => ($isAdmin ? '#fff' : '#000')};
  align-self: ${({ $isAdmin }) => ($isAdmin ? 'flex-end' : 'flex-start')};
  background: ${({ $isAdmin }) => ($isAdmin ? '#0427f4' : '#e8e8e8')};
  border-bottom-left-radius: ${({ $isAdmin }) => ($isAdmin ? '18px' : '4px')};
  border-bottom-right-radius: ${({ $isAdmin }) => ($isAdmin ? '4px' : '18px')};
  float: ${({ $isAdmin }) => ($isAdmin ? 'right' : 'left')};
  clear: both;
`;

const ChatInputContainer = styled.div`
  display: flex;
  gap: 8px;
`;

// --- Type Definitions ---
interface OnlineUser {
  id: string;
  status: string;
  isAnonymous: boolean;
  last_seen: any;
}

interface Message {
  id: string;
  text: string;
  senderId: string;
  timestamp: any;
}

// --- The LiveSupport Component ---
const LiveSupport: React.FC = () => {
  const { firestore } = useFirebase();
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<OnlineUser | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  
  // Effect 1: Listen for online users
  useEffect(() => {
    const usersStatusQuery = query(collection(firestore, 'user_status'), where('status', '==', 'online'));
    const unsubscribe = onSnapshot(usersStatusQuery, (snapshot) => {
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as OnlineUser));
      setOnlineUsers(users);
    });
    return () => unsubscribe();
  }, [firestore]);

  // Effect 2: Listen for messages when a chat is selected
  useEffect(() => {
    if (!chatId) return;
    const messagesQuery = query(collection(firestore, `chats/${chatId}/messages`), orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Message));
      setMessages(msgs);
    });
    return () => unsubscribe();
  }, [chatId, firestore]);

  const handleSelectUser = async (user: OnlineUser) => {
    setSelectedUser(user);
    setMessages([]); // Clear previous messages
    
    // Check if a chat already exists
    const chatQuery = query(collection(firestore, 'chats'), where('userId', '==', user.id), where('status', '!=', 'closed'));
    const existingChats = await getDocs(chatQuery);
    
    if (!existingChats.empty) {
      // Chat exists, load it
      const chatDoc = existingChats.docs[0];
      setChatId(chatDoc.id);
    } else {
      // No active chat, so we don't set a chatId until admin sends the first message
      setChatId(null); 
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;
    
    let currentChatId = chatId;

    // If no chat exists yet, create one
    if (!currentChatId) {
      const chatRef = await addDoc(collection(firestore, 'chats'), {
        userId: selectedUser.id,
        adminId: "admin", // Replace with actual admin UID from auth store if available
        status: 'active',
        createdAt: serverTimestamp(),
        lastMessage: newMessage,
        unreadByUser: true,
        unreadByAdmin: false,
      });
      currentChatId = chatRef.id;
      setChatId(currentChatId);
    }
    
    // Add message to the subcollection
    await addDoc(collection(firestore, `chats/${currentChatId}/messages`), {
      text: newMessage,
      senderId: "admin", // Replace with actual admin UID
      timestamp: serverTimestamp(),
    });
    
    // Update the parent chat document
    await updateDoc(doc(firestore, 'chats', currentChatId), {
      lastMessage: newMessage,
      lastUpdated: serverTimestamp(),
      unreadByUser: true,
    });
    
    setNewMessage('');
  };

  return (
    <LiveSupportContainer>
      <UserListPanel title="Online Users">
        <List
          dataSource={onlineUsers}
          renderItem={(user) => (
            <List.Item
              actions={[<Button type="primary" size="small" onClick={() => handleSelectUser(user)}>Chat</Button>]}
              style={{ background: selectedUser?.id === user.id ? '#e6f7ff' : 'transparent', padding: '12px' }}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={<Text>{user.isAnonymous ? 'Guest User' : 'Registered User'}</Text>}
                description={<Paragraph copyable={{ text: user.id }} style={{ margin: 0, fontSize: '12px' }}>ID: {user.id.substring(0, 8)}...</Paragraph>}
              />
            </List.Item>
          )}
        />
      </UserListPanel>
      <ChatPanel title={selectedUser ? `Chat with ${selectedUser.isAnonymous ? 'Guest' : 'User'} ${selectedUser.id.substring(0,4)}...` : 'Select a User to Chat'}>
        {selectedUser ? (
          <>
            <MessageList>
              {messages.map(msg => (
                <MessageBubble key={msg.id} $isAdmin={msg.senderId === 'admin'}>
                  {msg.text}
                </MessageBubble>
              ))}
              <div style={{ clear: 'both' }}></div>
            </MessageList>
            <ChatInputContainer>
              <Input
                placeholder="Type your message to initiate or continue chat..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onPressEnter={handleSendMessage}
              />
              <Button type="primary" icon={<SendOutlined />} onClick={handleSendMessage} disabled={!newMessage.trim()} />
            </ChatInputContainer>
          </>
        ) : (
          <Empty description="No user selected. Please select an online user from the left panel to begin a chat." />
        )}
      </ChatPanel>
    </LiveSupportContainer>
  );
};

export default LiveSupport;